import React from 'react';
import { Spinner } from '../UI/Spinner';
import { Button } from '../UI/Button';
import type { OrderDetailResponse, OrderStatus } from '../../types/api';
import { formatStatus, formatSide, formatOrderType } from '../../utils/orderFormat';

interface OrdersTableProps {
  orders: OrderDetailResponse[];
  isLoading: boolean;
  onCancelOrder: (order: OrderDetailResponse) => void;
  onAmendOrder: (order: OrderDetailResponse) => void;
  onViewDetails: (order: OrderDetailResponse) => void;
  pagination: {
    total: number;
    offset: number;
    limit: number;
    onPageChange: (offset: number) => void;
  };
}

/**
 * Status badge color configuration
 */
const STATUS_STYLES: Record<OrderStatus, { bg: string; text: string; icon: string }> = {
  pending_new: { bg: 'bg-gray-100', text: 'text-gray-800', icon: '⏳' },
  accepted: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '✓' },
  new: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '●' },
  partially_filled: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: '◐' },
  filled: { bg: 'bg-green-100', text: 'text-green-800', icon: '✓' },
  canceled: { bg: 'bg-gray-100', text: 'text-gray-500', icon: '✕' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: '✕' },
  pending_cancel: { bg: 'bg-orange-100', text: 'text-orange-800', icon: '⏳' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-500', icon: '⏱' },
  suspended: { bg: 'bg-gray-100', text: 'text-gray-500', icon: '⏸' },
  pending_replace: { bg: 'bg-purple-100', text: 'text-purple-800', icon: '⏳' },
  replaced: { bg: 'bg-gray-100', text: 'text-gray-500', icon: '↻' },
};

/**
 * Statuses that can be canceled/amended
 */
const CANCELLABLE_STATUSES: OrderStatus[] = ['pending_new', 'accepted', 'new', 'partially_filled'];

/**
 * Check if an order can be canceled or amended
 */
function isCancellable(status: OrderStatus): boolean {
  return CANCELLABLE_STATUSES.includes(status);
}

/**
 * Formats a date for display as relative time or date
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Status Badge component
 */
function StatusBadge({ status }: { status: OrderStatus }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.pending_new;
  const label = formatStatus(status);

  return (
    <span
      role="status"
      aria-label={`Order status: ${label}`}
      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${style.bg} ${style.text}`}
    >
      <span className="mr-1">{style.icon}</span>
      {label}
    </span>
  );
}

/**
 * Orders Table component
 * Displays orders with sorting, filtering, and row actions
 */
export function OrdersTable({
  orders,
  isLoading,
  onCancelOrder,
  onAmendOrder,
  onViewDetails,
  pagination,
}: OrdersTableProps) {
  const { total, offset, limit, onPageChange } = pagination;
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-8">
        <div className="flex justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-2 text-lg font-medium text-gray-900">No orders found</h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by submitting your first order.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" aria-label="Orders list">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Client ID
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Symbol
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Side
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Type
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Qty (Filled)
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Price
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Created
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.map((order) => (
              <tr
                key={order.id}
                className="hover:bg-gray-50 cursor-pointer"
                onDoubleClick={() => onViewDetails(order)}
              >
                <td className="px-4 py-4 whitespace-nowrap">
                  <span
                    className="text-sm text-gray-900 font-mono truncate max-w-[150px] inline-block"
                    title={order.client_order_id}
                  >
                    {order.client_order_id.length > 16
                      ? `${order.client_order_id.slice(0, 16)}...`
                      : order.client_order_id}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                  {order.symbol}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      order.side === 'buy'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {formatSide(order.side)}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatOrderType(order.order_type)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  <span className="font-medium">{order.filled_quantity}</span>
                  <span className="text-gray-500">/{order.quantity}</span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  {order.limit_price ? `$${order.limit_price}` : '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <StatusBadge status={order.status} />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span title={new Date(order.created_at).toLocaleString()}>
                    {formatDate(order.created_at)}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end space-x-2">
                    {/* View Details */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewDetails(order);
                      }}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="View details"
                      aria-label={`View details for order ${order.client_order_id}`}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>

                    {/* Cancel */}
                    {isCancellable(order.status) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancelOrder(order);
                        }}
                        className="text-red-400 hover:text-red-600 p-1"
                        title="Cancel order"
                        aria-label={`Cancel order ${order.client_order_id}`}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}

                    {/* Amend */}
                    {isCancellable(order.status) && order.order_type === 'limit' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAmendOrder(order);
                        }}
                        className="text-blue-400 hover:text-blue-600 p-1"
                        title="Amend order"
                        aria-label={`Amend order ${order.client_order_id}`}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <Button
              variant="secondary"
              onClick={() => onPageChange(Math.max(0, offset - limit))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              onClick={() => onPageChange(offset + limit)}
              disabled={currentPage >= totalPages}
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">{offset + 1}</span>
                {' '}to{' '}
                <span className="font-medium">{Math.min(offset + limit, total)}</span>
                {' '}of{' '}
                <span className="font-medium">{total}</span>
                {' '}orders
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(0, offset - limit))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => onPageChange(offset + limit)}
                  disabled={currentPage >= totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
